import { withProps } from "@udecode/cn";
import { TElement } from "@udecode/plate-common";
import {
    ParagraphPlugin as BaseParagraphPlugin,
    BoldPlugin,
    Plate,
    PlateElement,
    PlateLeaf,
    createPlatePlugin,
    usePlateEditor,
} from "@udecode/plate/react";
import { BoldIcon, Heading1 } from "lucide-react";
import { useId } from "react";
import { z } from "zod";

import ToggleBlockToolbarButton from "@/components/RichTextEditor/ToggleBlockToolbarButton";
import { Editor, EditorContainer } from "@/components/plate-ui/editor";
import { FixedToolbar } from "@/components/plate-ui/fixed-toolbar";
import { MarkToolbarButton } from "@/components/plate-ui/mark-toolbar-button";
import { ParagraphElement } from "@/components/plate-ui/paragraph-element";
import { ToolbarGroup } from "@/components/plate-ui/toolbar";
import { Label } from "@/components/ui/label";
import { cx } from "@/utils/cx";

const zRichText = z.array(
    z.object({
        type: z.union([z.literal("paragraph"), z.literal("heading")]),
        children: z.array(
            z.object({
                text: z.string(),
                bold: z.literal(true).optional(),
                italic: z.literal(true).optional(),
            })
        ),
    })
);

const EMPTY_EDITOR_CONTENT = zRichText.parse([
    {
        type: "paragraph",
        children: [{ text: "" }],
    },
]);

const ParagraphPlugin = BaseParagraphPlugin.configure({
    node: {
        isElement: true,
        type: "paragraph",
        component: ParagraphElement,
    },
});

const HeadingPlugin = createPlatePlugin({
    key: "heading",
    node: {
        isElement: true,
        type: "heading",
        component: withProps(PlateElement, {
            as: "h1",
            className: "font-planar15 text-lg",
        }),
    },
    shortcuts: {
        toggleHeading: {
            keys: ["Mod+Shift+1", "Mod+Alt+1"],
            handler: ({ editor }) => {
                editor.tf.toggle.block({ type: "heading" });
            },
        },
    },
});

export default function EditorInput({
    initialValue,
    onChange,
    className,
    label,
}: {
    initialValue?: TElement[] | null;
    onChange: (value: TElement[]) => void;
    className?: string;
    label?: string;
}) {
    const editor = usePlateEditor({
        value: initialValue ?? EMPTY_EDITOR_CONTENT,
        plugins: [ParagraphPlugin, HeadingPlugin, BoldPlugin],
        override: {
            components: {
                bold: withProps(PlateLeaf, { as: "strong" }),
            },
        },
    });

    const id = useId();

    return (
        <div className={cx("mt-2 flex flex-col gap-2", className)}>
            {label && <Label htmlFor={id}>{label}</Label>}
            <Plate
                editor={editor}
                onChange={({ value }) => onChange?.(value)}
                data-registry="plate"
            >
                <EditorContainer variant="default">
                    <FixedToolbar>
                        <ToolbarGroup>
                            <ToggleBlockToolbarButton blockType="heading" tooltip="Heading (⌘+⇧+1)">
                                <Heading1 />
                            </ToggleBlockToolbarButton>
                            <MarkToolbarButton nodeType={BoldPlugin.key} tooltip="Bold (⌘+B)">
                                <BoldIcon />
                            </MarkToolbarButton>
                        </ToolbarGroup>
                    </FixedToolbar>
                    <Editor
                        className="px-4 sm:px-4"
                        style={{ background: "#1E1E1E", color: "#f4f4f4" }}
                    />
                </EditorContainer>
            </Plate>
        </div>
    );
}
